import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, User, MessageSquare, MessageCircle } from 'lucide-react';

const Contacto = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    empresa: '',
    asunto: '',
    mensaje: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Crear el contenido del email
    const emailContent = `
NUEVO MENSAJE DE CONTACTO - REZCOM INTERNATIONAL

=== INFORMACIÓN DEL REMITENTE ===
Nombre: ${formData.nombre}
Empresa: ${formData.empresa || 'No especificada'}
Email: ${formData.email}
Teléfono: ${formData.telefono}

=== CONSULTA ===
Asunto: ${formData.asunto}

Mensaje:
${formData.mensaje}

=== DATOS ADICIONALES ===
Fecha: ${new Date().toLocaleString('es-MX')}
Origen: Formulario de Contacto Web

---
Para responder al cliente, utiliza: ${formData.email}
    `.trim();

    try {
      // Crear enlace mailto
      const subject = `Contacto Web - ${formData.asunto} - ${formData.nombre}`;
      const body = encodeURIComponent(emailContent);
      const mailtoLink = `mailto:contacto@rezcom-intl.mx?subject=${encodeURIComponent(subject)}&body=${body}`;

      // Abrir cliente de email
      window.location.href = mailtoLink;

      // Opción de WhatsApp como respaldo
      setTimeout(() => {
        const whatsappMessage = `Hola REZCOM! 

*Nombre:* ${formData.nombre}
*Empresa:* ${formData.empresa || 'No especificada'}
*Email:* ${formData.email}
*Asunto:* ${formData.asunto}

*Mensaje:*
${formData.mensaje}

¡Gracias!`;

        const useWhatsApp = window.confirm(
          "✅ Tu mensaje se ha enviado por email.\n\n¿También quieres enviarlo por WhatsApp?"
        );
        
        if (useWhatsApp) {
          const whatsappURL = `https://wa.me/5215562152214?text=${encodeURIComponent(whatsappMessage)}`;
          window.open(whatsappURL, '_blank');
        }
      }, 1000);

      alert('Mensaje enviado exitosamente. Te contactaremos pronto.');
      setFormData({
        nombre: '',
        email: '',
        telefono: '',
        empresa: '',
        asunto: '',
        mensaje: ''
      });
      
    } catch (error) {
      alert('Error al enviar el mensaje. Por favor intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleWhatsAppClick = () => {
    const message = "¡Hola REZCOM! Me interesa conocer más sobre sus servicios de logística internacional. ¿Podrían ayudarme?";
    const whatsappURL = `https://wa.me/5215562152214?text=${encodeURIComponent(message)}`;
    window.open(whatsappURL, '_blank');
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Teléfono",
      info: "+52 556 215 2214",
      description: "Lunes a Viernes: 8:00 AM - 6:00 PM",
      color: "from-green-500 to-green-700"
    },
    {
      icon: Mail,
      title: "Email",
      info: "contacto@rezcom-intl.mx",
      description: "Respuesta garantizada en 24 horas",
      color: "from-blue-500 to-blue-700"
    },
    {
      icon: MapPin,
      title: "Ubicación",
      info: "Ciudad de México, México",
      description: "Sede central y centro de operaciones",
      color: "from-purple-500 to-purple-700"
    },
    {
      icon: Clock,
      title: "Horario de Atención",
      info: "24/7 Servicio de Emergencia",
      description: "Soporte continuo para envíos urgentes",
      color: "from-orange-500 to-red-500"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Botón flotante de WhatsApp */}
      <motion.button
        onClick={handleWhatsAppClick}
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-2xl transition-all duration-300"
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 1.5, duration: 0.6, type: "spring" }}
        whileHover={{ 
          scale: 1.1,
          boxShadow: "0 0 30px rgba(34, 197, 94, 0.5)"
        }}
        whileTap={{ scale: 0.9 }}
      >
        <MessageCircle className="w-7 h-7" />
        <motion.div
          className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      </motion.button>

      <motion.section 
        className="bg-gradient-to-r from-green-800 to-green-900 py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-7xl mx-auto text-center">
          <motion.h1 
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Contáctanos
          </motion.h1>
          <motion.p 
            className="text-xl sm:text-2xl text-gray-200 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Estamos aquí para ayudarte con todas tus necesidades de logística internacional
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-8"
          >
            <motion.button
              onClick={handleWhatsAppClick}
              className="inline-flex items-center space-x-2 bg-green-500 hover:bg-green-400 text-white px-8 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <MessageCircle className="w-6 h-6" />
              <span>¡Escríbenos por WhatsApp!</span>
            </motion.button>
          </motion.div>
        </div>
      </motion.section>

      <motion.section 
        className="py-16 px-4 sm:px-6 lg:px-8"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${info.color} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                  <info.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{info.title}</h3>
                <p className="text-green-600 font-semibold mb-2">{info.info}</p>
                <p className="text-sm text-gray-600">{info.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="bg-white p-8 rounded-2xl shadow-xl">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <MessageSquare className="w-6 h-6 mr-3 text-green-600" />
                  Envíanos un Mensaje
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre Completo *
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                        <input
                          type="text"
                          name="nombre"
                          value={formData.nombre}
                          onChange={handleChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Teléfono
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                        <input
                          type="tel"
                          name="telefono"
                          value={formData.telefono}
                          onChange={handleChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="+52 555 123 4567"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Empresa
                      </label>
                      <input
                        type="text"
                        name="empresa"
                        value={formData.empresa}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Opcional"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Asunto *
                    </label>
                    <select
                      name="asunto"
                      value={formData.asunto}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                    >
                      <option value="">Selecciona un asunto</option>
                      <option value="Cotización de Servicios">Solicitar Cotización</option>
                      <option value="Información General">Información de Servicios</option>
                      <option value="Seguimiento de Envío">Seguimiento de Envío</option>
                      <option value="Soporte Técnico">Soporte Técnico</option>
                      <option value="Alianzas Comerciales">Alianzas Comerciales</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mensaje *
                    </label>
                    <textarea
                      name="mensaje"
                      value={formData.mensaje}
                      onChange={handleChange}
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                      placeholder="Describe tu consulta o necesidad..."
                      required
                    />
                  </div>

                  <motion.button
                    type="submit"
                    disabled={isLoading}
                    className={`w-full flex items-center justify-center space-x-2 py-4 px-6 rounded-xl font-semibold transition-all duration-300 ${
                      isLoading
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-green-600 to-green-800 hover:from-green-700 hover:to-green-900 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                    }`}
                    whileHover={{ scale: isLoading ? 1 : 1.05 }}
                    whileTap={{ scale: isLoading ? 1 : 0.95 }}
                  >
                    {isLoading ? (
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>Enviar Mensaje</span>
                      </>
                    )}
                  </motion.button>
                </form>
              </div>
            </motion.div>

            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="bg-gradient-to-br from-green-100 to-blue-100 p-8 rounded-2xl">
                <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">¿Necesitas Ayuda Inmediata?</h3>
                
                <div className="space-y-6">
                  <div className="text-center">
                    <h4 className="font-bold text-gray-900 mb-2">📞 Línea Directa</h4>
                    <div className="text-3xl font-bold text-green-800 mb-2">+52 556 215 2214</div>
                    <p className="text-sm text-gray-600">Lun-Vie: 8:00 AM - 6:00 PM</p>
                    <p className="text-sm text-green-600 font-semibold">Emergencias 24/7</p>
                  </div>

                  <div className="border-t border-gray-300 pt-6">
                    <h4 className="font-bold text-gray-900 mb-4 text-center">💬 WhatsApp Business</h4>
                    <motion.button
                      onClick={handleWhatsAppClick}
                      className="w-full bg-green-500 hover:bg-green-600 text-white py-4 px-6 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <MessageCircle className="w-5 h-5" />
                      <span>Iniciar Chat</span>
                    </motion.button>
                    <p className="text-xs text-gray-600 text-center mt-2">Respuesta inmediata en horario laboral</p>
                  </div>

                  <div className="border-t border-gray-300 pt-6">
                    <h4 className="font-bold text-gray-900 mb-2 text-center">📧 Email Corporativo</h4>
                    <p className="text-center text-gray-700">contacto@rezcom-intl.mx</p>
                    <p className="text-xs text-gray-600 text-center mt-1">Respuesta garantizada en 24h</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-xl">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Horarios de Atención</h3>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-700">Lunes - Viernes</span>
                    <span className="font-semibold text-green-600">8:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-700">Sábados</span>
                    <span className="font-semibold text-green-600">9:00 AM - 2:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-700">Domingos</span>
                    <span className="text-gray-500">Cerrado</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-700">Emergencias</span>
                    <span className="font-semibold text-red-600">24/7</span>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-sm text-yellow-800">
                    <strong>Nota:</strong> Para emergencias fuera del horario laboral, 
                    contáctanos por WhatsApp para atención inmediata.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default Contacto;